import { FC, useCallback } from 'react';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { useNavigate } from '@tanstack/react-router';
import { EntityLink } from '../entity-link';
import Icon from '../icon';
import { EntityLinkAction, EntityLinkSize, EntityLinkVariant } from '../entity-link/helpers';
import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';
import useDrawer from '@/hooks/use-drawer.hook';

interface Props {
  service: PublicServiceMeta | null;
  subtext?: string;
  size?: EntityLinkSize;
  variant?: EntityLinkVariant;
  action?: EntityLinkAction;
  iconSize?: number;
  showLoader?: boolean;
  condensed?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  image: {
    borderRadius: theme.shape.borderRadius,
  },
}));

const ServiceLink: FC<Props> = ({
  service,
  subtext,
  iconSize,
  size = 'small',
  variant = 'text',
  action = 'drawer',
  condensed = false,
}) => {
  const { id, name, image, isF5WAIS } = service || {};
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();
  const styles = useStyles();

  const canNavigate = action !== 'none';

  // Handle navigation
  const handleClick = useCallback(() => {
    if (action === 'drawer') {
      openDrawer('public-service', { id });
      return;
    }

    navigate({
      to: '/config/public-services',
      search: { drawer: 'public-service', dp_id: id },
    });
  }, [id, navigate, openDrawer, action]);

  const displayName = name || 'Unknown Service';
  const hasImage = !!image;

  const ImageElement = hasImage ? (
    <img src={image} alt={displayName} className={styles.image} />
  ) : (
    <Icon name="question-mark" color="grey" size="small" />
  );

  const hasClick = !isF5WAIS && canNavigate;

  return (
    <EntityLink
      image={ImageElement}
      title={displayName}
      subtext={subtext}
      onClick={hasClick ? handleClick : undefined}
      size={size}
      variant={variant}
      iconSize={iconSize}
      condensed={condensed}
    />
  );
};

export default ServiceLink;
